import * as XLSX from "xlsx";

const NAME_KEYS = [
  "fund",
  "scheme",
  "schemename",
  "fundname",
  "name",
  "scrip",
  "security",
  "instrument",
];
const VALUE_KEYS = [
  "currentvalue",
  "marketvalue",
  "value",
  "amount",
  "valuation",
  "closingvalue",
  "closingbalance",
  "mktvalue",
  "presentvalue",
];
const UNIT_KEYS = ["units", "balanceunits", "closingunits", "quantity", "qty", "unit"];
const COST_KEYS = ["costvalue", "invested", "purchasevalue", "cost", "totalcost", "investedvalue"];

function normalizeKey(k) {
  return String(k || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function parseIndianNumber(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  let s = String(raw).trim();
  if (!s || /^na$/i.test(s) || s === "-" || s === "—") return null;
  s = s.replace(/[₹,\s]/g, "").replace(/\((.*)\)/, "-$1");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function pickColumn(headers, candidates) {
  const normalized = headers.map((h) => ({ raw: h, key: normalizeKey(h) }));
  for (const c of candidates) {
    const hit = normalized.find((h) => h.key === c || h.key.includes(c));
    if (hit) return hit.raw;
  }
  return null;
}

function rowsFromSheet(sheet) {
  const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
  if (!json.length) return [];

  const headers = Object.keys(json[0]);
  const nameCol = pickColumn(headers, NAME_KEYS);
  const valueCol = pickColumn(headers, VALUE_KEYS);
  const unitCol = pickColumn(headers, UNIT_KEYS);
  const costCol = pickColumn(headers, COST_KEYS);

  if (!nameCol) return [];

  return json
    .map((row, i) => {
      const fundName = String(row[nameCol] || "").trim();
      const amount = parseIndianNumber(valueCol ? row[valueCol] : null);
      const units = parseIndianNumber(unitCol ? row[unitCol] : null);
      const invested = parseIndianNumber(costCol ? row[costCol] : null);
      if (!fundName || fundName.length < 3) return null;
      if (!amount || amount <= 0) return null;
      // skip totals
      if (/total|grand|summary|sub\s*total/i.test(fundName)) return null;
      return {
        id: `row-${i}`,
        fundName,
        amount,
        units: units ?? "",
        invested: invested ?? "",
      };
    })
    .filter(Boolean);
}

async function parseWorkbook(buffer) {
  const wb = XLSX.read(buffer, { type: "array" });
  let best = [];
  for (const name of wb.SheetNames) {
    const rows = rowsFromSheet(wb.Sheets[name]);
    if (rows.length > best.length) best = rows;
  }
  return best;
}

async function parseCsvText(text) {
  const wb = XLSX.read(text, { type: "string" });
  return rowsFromSheet(wb.Sheets[wb.SheetNames[0]]);
}

/** Extract holdings from free-form / CAMS-like text */
function parseHoldingsFromText(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const holdings = [];
  const seen = new Set();

  // Pattern: Fund/Scheme name ... number (value) optionally with units nearby
  const valueRe =
    /(?:₹|INR|Rs\.?)?\s*([\d,]+\.\d{2}|[\d,]{4,})/g;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!/(fund|scheme|etf|plan|index|fof)/i.test(line)) continue;
    if (/folio|registrar|email|phone|pan|address|page\s*\d/i.test(line)) continue;

    const amounts = [...line.matchAll(valueRe)]
      .map((m) => parseIndianNumber(m[1]))
      .filter((n) => n != null && n >= 500);

    if (!amounts.length) {
      // look at next line for value
      const next = lines[i + 1] || "";
      const nextAmts = [...next.matchAll(valueRe)]
        .map((m) => parseIndianNumber(m[1]))
        .filter((n) => n != null && n >= 500);
      if (!nextAmts.length) continue;
      amounts.push(...nextAmts);
    }

    // Prefer largest plausible current value on the line
    const amount = Math.max(...amounts);
    let fundName = line
      .replace(valueRe, " ")
      .replace(/\bISIN\b[:\s]*[A-Z0-9]+/gi, " ")
      .replace(/\bINF[A-Z0-9]+\b/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // Strip trailing codes
    fundName = fundName.replace(/\s[-–]\s*(Direct|Regular).*$/i, (m) => m).slice(0, 120);
    if (fundName.length < 8) continue;

    const key = fundName.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    holdings.push({
      id: `txt-${holdings.length}`,
      fundName,
      amount,
      units: "",
      invested: "",
    });
  }

  return holdings;
}

async function extractPdfText(file) {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const parts = [];
  const maxPages = Math.min(doc.numPages, 40);
  for (let p = 1; p <= maxPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    const strings = content.items.map((it) => it.str).filter(Boolean);
    parts.push(strings.join(" "));
  }
  return parts.join("\n");
}

/**
 * Parse an uploaded portfolio file into holdings.
 * @returns {{ holdings: Array, warnings: string[] }}
 */
export async function parsePortfolioFile(file) {
  if (!file) throw new Error("No file selected");
  if (file.size > 12 * 1024 * 1024) {
    throw new Error("File too large. Please upload a file under 12 MB.");
  }

  const name = file.name.toLowerCase();
  const warnings = [];

  try {
    if (name.endsWith(".csv") || file.type === "text/csv") {
      const text = await file.text();
      const holdings = await parseCsvText(text);
      if (!holdings.length) {
        const fallback = parseHoldingsFromText(text);
        if (fallback.length) {
          warnings.push("CSV headers were unclear — extracted holdings from text patterns.");
          return { holdings: fallback, warnings };
        }
        throw new Error(
          "Could not find fund holdings in this CSV. Include columns like Fund/Scheme Name and Current Value."
        );
      }
      return { holdings, warnings };
    }

    if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.includes("sheet")) {
      const buffer = await file.arrayBuffer();
      const holdings = await parseWorkbook(buffer);
      if (!holdings.length) {
        throw new Error(
          "Could not find holdings in this spreadsheet. Use columns: Fund/Scheme Name, Current Value (and optional Units)."
        );
      }
      return { holdings, warnings };
    }

    if (name.endsWith(".pdf") || file.type === "application/pdf") {
      const text = await extractPdfText(file);
      const holdings = parseHoldingsFromText(text);
      if (!holdings.length) {
        throw new Error(
          "Could not extract holdings from this PDF. Try downloading a CAMS/KFintech Excel/CSV, or use Manual Entry."
        );
      }
      warnings.push(
        "PDF extraction is best-effort. Review holdings below and edit amounts if needed."
      );
      return { holdings, warnings };
    }

    if (name.endsWith(".txt")) {
      const text = await file.text();
      const holdings = parseHoldingsFromText(text);
      if (!holdings.length) {
        throw new Error("No holdings found in this text file.");
      }
      return { holdings, warnings };
    }

    // Unknown extension — try excel then text
    try {
      const buffer = await file.arrayBuffer();
      const holdings = await parseWorkbook(buffer);
      if (holdings.length) return { holdings, warnings };
    } catch {
      /* fall through */
    }
    const text = await file.text();
    const holdings = parseHoldingsFromText(text);
    if (holdings.length) return { holdings, warnings };

    throw new Error("Unsupported file format. Upload PDF, Excel (.xlsx), or CSV.");
  } catch (err) {
    if (err?.message) throw err;
    throw new Error("Failed to read this file. Try Excel/CSV or Manual Entry.");
  }
}

export function normalizeManualHoldings(rows) {
  return rows
    .filter((r) => String(r.fundName || "").trim() && Number(r.amount) > 0)
    .map((r, i) => ({
      id: r.id || `manual-${i}`,
      fundName: String(r.fundName).trim(),
      amount: Number(r.amount),
      units: r.units === "" || r.units == null ? "" : Number(r.units),
      invested:
        r.invested === "" || r.invested == null ? "" : Number(r.invested),
    }));
}
